import { memo, useEffect, useCallback, useState } from "react";
import { Table, Button, Tooltip, message } from "antd";
import { ReloadOutlined, SearchOutlined, DownloadOutlined, ExpandOutlined, CompressOutlined, CloseOutlined } from "@ant-design/icons";
import { ColumnsType, TableProps } from "antd/es/table";
import { CollectFormAPIs, CollectForm_GetPage_RequestQuery } from "~/common/typescript/apis/CollectFormAPIs";
import { CollectForm_Base } from "~/common/typescript/models/CollectForm";
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import { currentProjectAtom } from "~/stores/ProjectStore";
import { userProfileModalAtom } from "~/components/Map/components/UserProfile/UserProfileStore";
import styled from "styled-components";
import MobileCardList from "../components/MobileCardList";
import { useScreenSize } from "~/hooks/useScreenSize";
import { formatDate } from "~/utils/dateUtils";
import DateTimeViewer from "~/components/common/DateTimeViewer";
import UUIDViewer from "~/components/common/UUIDViewer";
import BulkActionsBar from "../components/BulkActionsBar";
import { availableFormsAtom, collectFormsPaginationAtom } from "../atoms/CollectFormAtoms";

interface CollectFormsTabProps {
  onFormSelect?: (formUuid: string, formData?: any) => void;
  tableHeight?: string;
  selectedFormUuid?: string;
  onMaximize?: () => void;
  onClose?: () => void;
  isMaximized?: boolean;
}

const CollectFormsTab = ({ onFormSelect, tableHeight, selectedFormUuid, onMaximize, onClose, isMaximized }: CollectFormsTabProps) => {
  const currentProject = useAtomValue(currentProjectAtom);
  const setUserProfileModal = useSetAtom(userProfileModalAtom);
  const { isMobile } = useScreenSize();
  const [availableForms, setAvailableForms] = useAtom(availableFormsAtom);
  const [paginationInfo, setPaginationInfo] = useAtom(collectFormsPaginationAtom);
  const [loading, setLoading] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  const fetchCollectForms = useCallback(async () => {
    if (!currentProject?.id) return;

    setLoading(true);
    try {
      const request: CollectForm_GetPage_RequestQuery = {
        projectId: currentProject.id,
        page: paginationInfo.page,
        pageSize: isMobile ? 15 : 10,
      };

      const response = await CollectFormAPIs.remote_CollectForm_getPage(request);

      setAvailableForms(response.results);
      setPaginationInfo({
        page: response.pagination.page,
        pageSize: response.pagination.pageSize,
        pageCount: response.pagination.pageCount,
        total: response.pagination.total,
      });
    } catch (error) {
      console.error('Error fetching collect forms:', error);
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, paginationInfo.page]);

  // Initial fetch
  useEffect(() => {
    fetchCollectForms();
  }, []);

  // Update page size when screen size changes
  useEffect(() => {
    setPaginationInfo(prev => ({ ...prev, pageSize: isMobile ? 15 : 10, page: 1 }));
    fetchCollectForms();
  }, [isMobile]);


  const handleFormClick = (record: CollectForm_Base) => {
    if (onFormSelect && record.uuid) {
      onFormSelect(record.uuid, record);
    }
  };

  const mobileCardData = availableForms.map((form) => ({
    ...form,
    id: form.uuid || '',
    title: form.name || 'Chưa có tên',
    description: form.description || 'Không có mô tả',
    date: formatDate(form.created_at),
  }));


  const columns: ColumnsType<CollectForm_Base> = [
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      width: 100,
      render: (uuid: string) => <UUIDViewer uuid={uuid} />,
    },
    {
      title: 'Tên biểu mẫu',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <span style={{ fontSize: '14px', fontWeight: 500 }}>
          {name || 'Chưa có tên'}
        </span>
      ),
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => (
        <span style={{ fontSize: '12px', color: '#666' }}>
          {description ? description.substring(0, 100) + (description.length > 100 ? '...' : '') : 'Không có mô tả'}
        </span>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => (
        <span style={{ fontSize: '12px' }}>
          <DateTimeViewer date={date} initialMode='relative' />
        </span>
      ),
    },
    {
      title: 'Hành động',
      // dataIndex: 'created_at',
      // key: 'created_at',
      width: 120,
      render: (date: string, record: any) => (
        <div style={{ fontSize: '12px' }}>
          <Button type="link" size="small"  onClick={() => {
            handleFormClick(record);
          }} >
            Xem
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    fetchCollectForms();
  }, [fetchCollectForms]);

  // Multiselect functions
  const handleBulkDelete = useCallback(async () => {
    if (selectedRowKeys.length === 0) return;

    setBulkDeleteLoading(true);
    try {
      const response = await CollectFormAPIs.remote_bulkDelete({
        uuids: selectedRowKeys as string[],
      });
      message.success(`Đã xóa ${response.data.deleted_count} biểu mẫu thành công`);
      setSelectedRowKeys([]);
      await fetchCollectForms();
    } catch (error: any) {
      console.error('Error bulk deleting collect forms:', error);
      message.error(error?.response?.data?.message || 'Lỗi khi xóa biểu mẫu');
    } finally {
      setBulkDeleteLoading(false);
    }
  }, [selectedRowKeys, fetchCollectForms]);

  const handleBulkDownload = useCallback((format: 'xlsx' | 'geojson' | 'csv' | 'shp') => {
    message.info(`Tính năng tải xuống ${selectedRowKeys.length} biểu mẫu đang được phát triển`);
  }, [selectedRowKeys]);

  const handleSelectAll = useCallback(() => {
    const allKeys = availableForms.map((form) => form.uuid);
    setSelectedRowKeys(allKeys);
  }, [availableForms]);

  const handleClearSelection = useCallback(() => {
    setSelectedRowKeys([]);
  }, []);

  const rowSelection: TableProps<CollectForm_Base>['rowSelection'] = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  return (
    <Container>
      <ControlRow>
        <RecordCount>Số bản ghi: {paginationInfo.total}</RecordCount>
        <ControlActions>
          <Tooltip title="Tìm kiếm">
            <Button
              type="text"
              size="small"
              icon={<SearchOutlined />}
              onClick={() => {}}
            />
          </Tooltip>
          <Tooltip title="Tải xuống">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => {}}
            />
          </Tooltip>
          <Tooltip title="Làm mới danh sách">
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchCollectForms}
            />
          </Tooltip>
          {onMaximize && (
            <Tooltip title={isMaximized ? "Thu nhỏ" : "Phóng to"}>
              <Button
                type="text"
                size="small"
                icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={onMaximize}
              />
            </Tooltip>
          )}
          {onClose && (
            <Tooltip title="Đóng">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
              />
            </Tooltip>
          )}
        </ControlActions>
      </ControlRow>

      {!isMobile && selectedRowKeys.length > 0 && (
        <BulkActionsBar
          selectedCount={selectedRowKeys.length}
          totalCount={availableForms.length}
          onBulkDelete={handleBulkDelete}
          onDownload={handleBulkDownload}
          onClearSelection={handleClearSelection}
          onSelectAll={handleSelectAll}
          loading={bulkDeleteLoading}
          showDownload={false}
        />
      )}

      {isMobile ? (
        <MobileCardList
          data={mobileCardData}
          loading={loading}
          selectedId={selectedFormUuid}
          onItemClick={(item) => {
            const form = availableForms.find(f => f.uuid === item.id);
            if (form) handleFormClick(form);
          }}
          onCreatorClick={(creatorId) => {
            setUserProfileModal({
              visible: true,
              userId: parseInt(creatorId)
            });
          }}
          pagination={{
            current: paginationInfo.page,
            pageSize: paginationInfo.pageSize,
            total: paginationInfo.total,
            onChange: (page, pageSize) => {
              setPaginationInfo({ ...paginationInfo, page, pageSize });
            },
          }}
        />
      ) : (
        <Table
          dataSource={availableForms}
          columns={columns}
          rowKey={'uuid'}
          rowSelection={rowSelection}
          bordered
          style={{
            cursor: 'pointer',
            flex: 1,
            width: '100%',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #f1f5f9'
          }}
          rowClassName={(record) => {
            return selectedFormUuid === record.uuid ? 'selected-form-row' : '';
          }}
          onRow={(record) => {
            return {
              onClick: () => {
                // handleFormClick(record);
              },
            };
          }}
          size="small"
          loading={loading}
          scroll={{ y: tableHeight ? `calc(${tableHeight} - 20px)` : undefined }}
          pagination={{
            current: paginationInfo.page,
            pageSize: paginationInfo.pageSize,
            total: paginationInfo.total,
            pageSizeOptions: [10, 20, 50, 100],
            position: ["bottomCenter"],
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total}`,
            onChange: (page, pageSize) => {
              setPaginationInfo({ ...paginationInfo, page, pageSize });
            },
          }}
        />
      )}
    </Container>
  );
};

export default memo(CollectFormsTab);

const Container = styled.div`
  padding: 6px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 4px;
  }

  .ant-table-thead > tr > th {
    font-size: 10px !important;
    padding: 4px !important;
  }

  .ant-table-tbody > tr > td {
    padding: 4px !important;
  }

  .ant-table-measure-row td {
    padding: 0 !important;
  }
`;

const ControlRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 6px;

  @media (max-width: 768px) {
    padding: 4px 8px;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const RecordCount = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`;

const ControlActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CreatorName = styled.span`
  font-size: 12px;
  color: #3b82f6;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
`;